import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckIcon, CloseIcon, PlusIcon, useToast } from "@signet/ui";
import { subitemApi, todoKeys, type Subitem, type Todo } from "./api.ts";
import { TickBurst, TickMark, useLeave } from "./tick.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Sub-items (Jay, 2026-10-29): a checklist above a to-do's notes, in the
   panel. Ticking one plays the same tick as a to-do on the list (tick.tsx),
   then it swipes out and comes back struck through under the open ones.
   ────────────────────────────────────────────────────────────────────────── */

const messageOf = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong.");

/** Puts the to-do the server sent back into every list at once, then reads them all again. */
function useApply() {
  const client = useQueryClient();
  return (item: Todo) => {
    client.setQueriesData<{ items?: Todo[] }>({ queryKey: todoKeys.all }, (old) =>
      old?.items ? { ...old, items: old.items.map((t) => (t.id === item.id ? item : t)) } : old,
    );
    void client.invalidateQueries({ queryKey: todoKeys.all });
  };
}

export function Subitems({ todo, editable }: { todo: Todo; editable: boolean }) {
  const apply = useApply();
  const toast = useToast();
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const open = todo.subitems.filter((s) => !s.done);
  const done = todo.subitems.filter((s) => s.done);
  if (!editable && !todo.subitems.length) return null;

  const add = async () => {
    if (!name.trim() || adding) return;
    setAdding(true);
    try {
      apply((await subitemApi.add(todo.id, name.trim())).item);
      setName("");
    } catch (error) {
      toast.error("Couldn't add it", messageOf(error));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="td-subs">
      <div className="td-notes-head">
        <span>Sub-items</span>
      </div>
      {todo.subitems.length ? (
        <ul className="td-sub-list">
          {/* Keyed by state too, so a ticked line comes back fresh under the open ones, not folded away. */}
          {[...open, ...done].map((s) => (
            <SubitemRow key={`${s.id}:${s.done}`} todo={todo} sub={s} editable={editable} />
          ))}
        </ul>
      ) : null}
      {editable ? (
        <form
          className="td-sub-add"
          onSubmit={(e) => {
            e.preventDefault();
            void add();
          }}
        >
          <PlusIcon size={15} />
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Add a sub-item and press Enter" aria-label="New sub-item" maxLength={300} disabled={adding} />
        </form>
      ) : null}
    </div>
  );
}

function SubitemRow({ todo, sub, editable }: { todo: Todo; sub: Subitem; editable: boolean }) {
  const apply = useApply();
  const toast = useToast();
  const leave = useLeave<HTMLLIElement>();
  const [name, setName] = useState(sub.name);
  const [pending, setPending] = useState(false);

  useEffect(() => setName(sub.name), [sub.name]);

  const tick = async () => {
    setPending(true);
    const leaving = !sub.done;
    try {
      // Applied only once both are over, so the line isn't moved out from under its own swipe.
      const [result] = await Promise.all([subitemApi.update(todo.id, sub.id, { done: !sub.done }), leaving ? leave.play() : null]);
      apply(result.item);
    } catch (error) {
      if (leaving) leave.undo();
      toast.error("Couldn't save that", messageOf(error));
    } finally {
      setPending(false);
    }
  };

  const rename = () => {
    const next = name.trim();
    if (!next || next === sub.name) return setName(sub.name);
    subitemApi.update(todo.id, sub.id, { name: next }).then((r) => apply(r.item), (error) => {
      setName(sub.name);
      toast.error("Couldn't save that", messageOf(error));
    });
  };

  const remove = () =>
    subitemApi.remove(todo.id, sub.id).then((r) => apply(r.item), (error) => toast.error("Couldn't remove it", messageOf(error)));

  return (
    <li ref={leave.ref} className={`td-sub${sub.done ? " done" : ""}${leave.active ? " td-ticking" : ""}`}>
      {leave.active ? <span className="td-shine" aria-hidden="true" /> : null}
      <button
        type="button"
        className="td-check"
        role="checkbox"
        aria-checked={sub.done || leave.active}
        aria-label={sub.done ? `Mark "${sub.name}" not done` : `Tick off "${sub.name}"`}
        disabled={!editable || pending}
        onClick={() => void tick()}
      >
        {leave.active ? (
          <>
            <TickBurst />
            <TickMark />
          </>
        ) : sub.done ? (
          <CheckIcon size={13} />
        ) : null}
      </button>
      <span className="td-sub-name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={rename}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            if (e.key === "Escape") {
              setName(sub.name);
              (e.target as HTMLInputElement).blur();
            }
          }}
          readOnly={!editable}
          maxLength={300}
          aria-label="Sub-item"
        />
      </span>
      {editable ? (
        <button type="button" className="td-sub-remove" aria-label={`Remove "${sub.name}"`} title="Remove" onClick={() => void remove()}>
          <CloseIcon size={13} />
        </button>
      ) : null}
    </li>
  );
}
